import { useContext } from 'react'
import { CustomerBM, DeployStatus } from '@bm-js/atlascloud-shared'
import { _assert } from '@naturalcycles/js-lib/error'
import { deployStatusColorMap, deployStatusTextMap } from './website.utils'
import { ToastContext } from '../contexts/Toast.context'
import { PopupContext } from '../contexts/Popup.context'
import { ActionsContext } from '../contexts/Actions.context'
import { AuthContext } from '../contexts/AuthState.context'
import { AppContext } from '../contexts/AppState.context'
import { useApi } from './useApi'
import { EditorContext } from '../contexts/Editor.context'
import { ConfirmContext } from '../contexts/Confirm.context'
import { CmsEditorContext } from '../contexts/CmsEditor.context'
import { MediaContext } from '../contexts/Media.context'

export const useToken = () => {
  const { token } = useAuth()
  return token
}

export const useUser = () => {
  const { user } = useAuth()
  return user
}

export const useActions = () => {
  const actions = useContext(ActionsContext)
  return actions
}

export const useAuth = () => {
  const { state } = useContext(AuthContext)
  return state
}

export const useAuthDispatch = () => {
  const { dispatch } = useContext(AuthContext)
  return dispatch
}

export const useWebsiteStatus = () => {
  const INTERVAL_TIME = 10_000
  const { atlasWebsite } = useWebsite()
  const token = useToken()

  const { data } = useApi<{
    siteLink?: string
    screenshot?: string
    status?: DeployStatus | 'pending'
  }>(`customer/website/${atlasWebsite?._id}/build-status`, {
    enabled: !!atlasWebsite,
    queryKeys: [atlasWebsite?._id, token],
    refetchInterval({ state: { data } }) {
      const { status } = data || {}
      if (status && ['ready', 'error'].includes(status)) return false
      return INTERVAL_TIME
    },
  })

  const { screenshot, siteLink, status = 'pending' } = data || {}

  return {
    screenshot,
    deployStatus: {
      text: deployStatusTextMap[status],
      color: deployStatusColorMap[status],
    },
    siteLink,
  }
}

const useAppState = () => {
  const state = useContext(AppContext)
  return state
}

export function useCustomer(opts?: { required?: false }): CustomerBM | null
export function useCustomer(opts: { required: true }): CustomerBM
export function useCustomer(opts?: { required?: boolean }): CustomerBM | null {
  const { customer } = useAppState()
  if (opts?.required) {
    _assert(customer, 'useCustomer: customer is required but not defined')
  }
  return customer
}

export const useWebsite = () => {
  const {
    atlasWebsite,
    refetchAtlasWebsite,
    cmsWebsite,
    refetchCmsWebsite,
    anyWebsite,
  } = useAppState()
  const { triggerRebuild } = useActions()
  return {
    atlasWebsite,
    refetchAtlasWebsite,
    triggerRebuild,
    cmsWebsite,
    refetchCmsWebsite,
    anyWebsite,
  }
}

export const useToast = () => {
  const { toast } = useContext(ToastContext)
  return toast
}

export const usePopup = () => {
  const popupState = useContext(PopupContext)
  return popupState
}

export const useEditorState = () => {
  const editorState = useContext(EditorContext)
  return editorState
}

export const useEditorData = () => {
  const { editorData } = useEditorState()
  if (!editorData) throw new Error('editorData not set in useEditorData!')
  return editorData
}

export const useEditorStatus = () => {
  const { status } = useEditorState()
  return status
}

export const useEditorUi = () => {
  const { ui } = useEditorState()
  return ui
}

export const useEditorActions = () => {
  const { actions } = useEditorState()
  return actions
}

export const useEditorWebsite = () => {
  const { backendResponse } = useEditorData()
  return backendResponse.website
}

export const useConfirmation = () => {
  const confirmation = useContext(ConfirmContext)
  return confirmation
}

const useCmsEditor = () => {
  const state = useContext(CmsEditorContext)
  return state
}

export const useCmsEditorData = () => {
  const { editorData } = useCmsEditor()
  if (!editorData) throw new Error('editorData not set in useCmsEditorData!')
  return editorData
}

export const useCmsEditorStatus = () => {
  const { status } = useCmsEditor()
  return status
}

export const useCmsEditorActions = () => {
  const { actions } = useCmsEditor()
  return actions
}

export const useMedia = () => {
  const media = useContext(MediaContext)
  return media
}
